import { Component } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';

import { getUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';

import {
    NUMBER_OF_PRODUCT_IMPRESSIONS,
    trackEcommerceProductImpressions,
    trackEcommerceProductClick,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';
import { getMergedSbAndSponsoredItems } from '../helpers/getMergedSbAndSponsoredItems';

class SbSharedItemTrackingProviderComponent extends Component {
    constructor() {
        super();

        this.fireItemImpressionTracking = this.fireItemImpressionTracking.bind(this);
        this.fireItemClickTracking = this.fireItemClickTracking.bind(this);
        this.trackedItemImpressionIndexes = [];
        this.attemptedItemImpressionIndexes = [];
        this.sponsoredItemsIndexMetadataMap = null;
        this.items = [];
    }

    componentDidMount() {
        const { itemSearch, mergeSbAndSponsoredItemsArgs } = this.props;
        const { displayUriRef, edges, sponsored } = itemSearch;

        if (mergeSbAndSponsoredItemsArgs !== null) {
            const { items: mergedItems, sponsoredItemsIndexMetadataMap } =
                getMergedSbAndSponsoredItems({
                    items: edges,
                    sponsored,
                    displayUriRef,
                    ...mergeSbAndSponsoredItemsArgs,
                });
            this.items = mergedItems;
            this.sponsoredItemsIndexMetadataMap = sponsoredItemsIndexMetadataMap;
        } else {
            this.items = edges;
        }

        this.fireItemImpressionTracking(0);
    }

    componentDidUpdate(prevProps) {
        if (this.props.itemSearch.displayUriRef !== prevProps.itemSearch.displayUriRef) {
            /**
             * Assume the page changed and reset tracking data so that we're able to fire tracking
             * for the new set of items.
             */
            const { itemSearch, mergeSbAndSponsoredItemsArgs } = this.props;
            const { displayUriRef, edges, sponsoredItems } = itemSearch;

            if (mergeSbAndSponsoredItemsArgs !== null) {
                const { items: mergedItems, sponsoredItemsIndexMetadataMap } =
                    getMergedSbAndSponsoredItems({
                        items: edges,
                        sponsoredItems,
                        displayUriRef,
                        ...mergeSbAndSponsoredItemsArgs,
                    });
                this.items = mergedItems;
                this.sponsoredItemsIndexMetadataMap = sponsoredItemsIndexMetadataMap;
            } else {
                this.items = edges;
            }

            this.trackedItemImpressionIndexes = [];
            this.fireItemImpressionTracking(0);
        }

        if (this.regionalInfoAvailable() && this.attemptedItemImpressionIndexes.length !== 0) {
            this.attemptedItemImpressionIndexes.forEach(attemptedItemImpressionIndex => {
                this.fireItemImpressionTracking(attemptedItemImpressionIndex);
            });
            this.attemptedItemImpressionIndexes = [];
        }
    }

    getRegions() {
        return (
            getUserSessionRegions() || this.props.viewer?.regionalInfo?.[0]?.regionsByZipCode || []
        );
    }

    regionalInfoAvailable() {
        return !!(getUserSessionRegions() || this.props.viewer?.regionalInfo);
    }

    trackProductImpression(startIndex) {
        const { searchCorrectionTerm, itemSearch, isRelatedSearchItems } = this.props;
        const { searchTerm, pageType } = itemSearch || {};
        const userRegions = this.getRegions();

        trackEcommerceProductImpressions({
            items: this.items,
            sponsoredItemsIndexMetadataMap: this.sponsoredItemsIndexMetadataMap,
            pageType,
            searchTerm,
            startIndex,
            searchCorrectionTerm,
            userRegions,
            isRelatedSearchItems,
        });
    }

    fireItemImpressionTracking(index) {
        /**
         * Group items from index 0 to 19 to group with the number `0`, from 20 to 39 to group
         * 1 and so on.
         */
        const impressionGroup = Math.floor(index / NUMBER_OF_PRODUCT_IMPRESSIONS);

        if (!this.regionalInfoAvailable()) {
            this.attemptedItemImpressionIndexes.push(impressionGroup);
        } else if (!this.trackedItemImpressionIndexes.includes(impressionGroup)) {
            /**
             * Very important to track indexes that have already fired - `react-lazy-load` can fire
             * `contentVisible` handler multiple times for the same item.
             */
            this.trackProductImpression(impressionGroup * NUMBER_OF_PRODUCT_IMPRESSIONS);
            this.trackedItemImpressionIndexes.push(impressionGroup);
        }
    }

    fireItemClickTracking(index) {
        const { searchCorrectionTerm, itemSearch } = this.props;
        const { searchTerm, pageType } = itemSearch || {};
        const itemNode = this.items?.[index]?.node;
        const item = itemNode?.item || itemNode;
        const userRegions = this.getRegions();

        trackEcommerceProductClick({
            item,
            index,
            pageType,
            searchTerm,
            searchCorrectionTerm,
            userRegions,
            sponsoredItemsIndexMetadataMap: this.sponsoredItemsIndexMetadataMap,
        });
    }

    render() {
        return this.props.children({
            fireItemImpressionTracking: this.fireItemImpressionTracking,
            fireItemClickTracking: this.fireItemClickTracking,
        });
    }
}

SbSharedItemTrackingProviderComponent.propTypes = {
    children: PropTypes.func,
    searchCorrectionTerm: PropTypes.string,
    itemSearch: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    isRelatedSearchItems: PropTypes.string,
    mergeSbAndSponsoredItemsArgs: PropTypes.shape({
        isTrade: PropTypes.bool,
        pageSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isMobile: PropTypes.bool,
    }),
};

/*
  Fields necessary for ecommerce tracking...
  serviceId
  title
  seller { serviceId }
  categoryPath
  contemporaryTrackingString
  displayInfo(buyerId: $userId) @include(if: $isClient) {
    pricing {
        showNet
        showSale
    }
  }
  pricing {
    amount {
        convertedAmountList {
            amount
            currency
        }
    }
    salePrice {
        convertedAmountList {
            amount
            currency
        }
    }
    netPrice {
        convertedAmountList {
            amount
            currency
        }
    }
  }
*/

export const SbSharedItemTrackingProvider = createFragmentContainer(
    SbSharedItemTrackingProviderComponent,
    {
        itemSearch: graphql`
            fragment SbSharedItemTrackingProvider_itemSearch on ItemSearchQueryConnection {
                pageType
                searchTerm
                displayUriRef

                sponsored {
                    items {
                        serviceId
                        title
                        categoryPath
                        browseUrl
                        categoryCode
                        contemporaryTrackingString
                        seller {
                            serviceId
                        }
                        displayPriceTracking: displayPrice(
                            page: searchAndBrowse
                            isTrade: $isTrade
                            userCountryCode: $userCountryCode
                            priceBookName: $priceBookName
                        ) {
                            convertedAmountList {
                                amount
                                currency
                            }
                            amountType
                            textType
                        }
                        isAuctionEnabled
                        isNewListing
                        ...shippingCostHelpers_item
                    }

                    metadata {
                        itemId
                        clickTrackerLink
                        impressionTrackerLink
                    }
                }

                edges {
                    node {
                        item {
                            serviceId
                            title
                            categoryPath
                            browseUrl
                            categoryCode
                            contemporaryTrackingString
                            seller {
                                serviceId
                            }
                            displayPriceTracking: displayPrice(
                                page: searchAndBrowse
                                isTrade: $isTrade
                                userCountryCode: $userCountryCode
                                priceBookName: $priceBookName
                            ) {
                                convertedAmountList {
                                    amount
                                    currency
                                }
                                amountType
                                textType
                            }
                            isAuctionEnabled
                            isNewListing
                            ...shippingCostHelpers_item
                        }
                    }
                }
            }
        `,
        viewer: graphql`
            fragment SbSharedItemTrackingProvider_viewer on Viewer {
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
    }
);
